import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import {
  AnalyzeUploadedTemplateMetadata,
  AnalyzeUploadedTemplateResponse,
} from '../models/document-asset-analysis.model';
import {
  DocumentAssetApiResponse,
  DocumentAssetListFilter,
  DocumentAssetSummary,
  RegisterAssetMetadata,
  UpdateAssetMetadata,
} from '../models/document-asset.model';
import { AssetReferenceReport } from '../models/document-template-binding.model';

/**
 * Client HTTP tipizzato per gli allegati riusabili V2
 * (`/api/v2/document-attachments`).
 */
@Injectable({ providedIn: 'root' })
export class DocumentAttachmentV2Service {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.backofficeApiUrl}/api/v2/document-attachments`;

  list(
    filter?: DocumentAssetListFilter,
  ): Observable<DocumentAssetApiResponse<DocumentAssetSummary[]>> {
    return this.http.get<DocumentAssetApiResponse<DocumentAssetSummary[]>>(
      this.baseUrl,
      { params: this.buildListParams(filter) },
    );
  }

  /**
   * Returns the next numeric progressive (1-based) to prefix a fresh attachment
   * businessKey (e.g. `003_MY_ATTACHMENT`).
   */
  getNextProgressive(): Observable<DocumentAssetApiResponse<number>> {
    return this.http.get<DocumentAssetApiResponse<number>>(
      `${this.baseUrl}/next-progressive`,
    );
  }

  get(
    assetId: number,
  ): Observable<DocumentAssetApiResponse<DocumentAssetSummary>> {
    return this.http.get<DocumentAssetApiResponse<DocumentAssetSummary>>(
      `${this.baseUrl}/${assetId}`,
    );
  }

  listVersions(
    businessKey: string,
  ): Observable<DocumentAssetApiResponse<DocumentAssetSummary[]>> {
    return this.http.get<DocumentAssetApiResponse<DocumentAssetSummary[]>>(
      `${this.baseUrl}/versions/${encodeURIComponent(businessKey)}`,
    );
  }

  references(
    assetId: number,
  ): Observable<DocumentAssetApiResponse<AssetReferenceReport>> {
    return this.http.get<DocumentAssetApiResponse<AssetReferenceReport>>(
      `${this.baseUrl}/${assetId}/references`,
    );
  }

  download(assetId: number): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/${assetId}/download`, {
      responseType: 'blob',
    });
  }

  analyzeUpload(
    file: File,
    metadata: AnalyzeUploadedTemplateMetadata,
  ): Observable<DocumentAssetApiResponse<AnalyzeUploadedTemplateResponse>> {
    const body = this.buildMultipart(file, metadata);
    return this.http.post<
      DocumentAssetApiResponse<AnalyzeUploadedTemplateResponse>
    >(`${this.baseUrl}/analyze-upload`, body);
  }

  analyzeRegistered(
    assetId: number,
  ): Observable<DocumentAssetApiResponse<AnalyzeUploadedTemplateResponse>> {
    return this.http.post<
      DocumentAssetApiResponse<AnalyzeUploadedTemplateResponse>
    >(`${this.baseUrl}/${assetId}/analyze`, null);
  }

  register(
    file: File,
    metadata: RegisterAssetMetadata,
  ): Observable<DocumentAssetApiResponse<DocumentAssetSummary>> {
    const body = this.buildMultipart(file, metadata);
    return this.http.post<DocumentAssetApiResponse<DocumentAssetSummary>>(
      `${this.baseUrl}/register`,
      body,
    );
  }

  update(
    assetId: number,
    metadata: UpdateAssetMetadata,
  ): Observable<DocumentAssetApiResponse<DocumentAssetSummary>> {
    return this.http.put<DocumentAssetApiResponse<DocumentAssetSummary>>(
      `${this.baseUrl}/${assetId}`,
      metadata,
    );
  }

  delete(
    assetId: number,
    force = false,
  ): Observable<DocumentAssetApiResponse<DocumentAssetSummary>> {
    let params = new HttpParams();
    if (force) params = params.set('force', 'true');
    return this.http.delete<DocumentAssetApiResponse<DocumentAssetSummary>>(
      `${this.baseUrl}/${assetId}`,
      { params },
    );
  }

  private buildListParams(filter?: DocumentAssetListFilter): HttpParams {
    let params = new HttpParams();
    if (!filter) return params;
    if (filter.domainType) params = params.set('domainType', filter.domainType);
    if (filter.status) params = params.set('status', filter.status);
    if (filter.includeInactive !== undefined) {
      params = params.set('includeInactive', String(filter.includeInactive));
    }
    return params;
  }

  private buildMultipart(file: File, metadata: object): FormData {
    const body = new FormData();
    body.append('file', file, file.name);
    body.append(
      'metadata',
      new Blob([JSON.stringify(metadata)], { type: 'application/json' }),
    );
    return body;
  }
}
